import { useTranslation } from 'react-i18next';

import { formatInstantAsDay } from 'support/helpers/dateTime/dateTime';
import { getTradePartner } from 'support/helpers/relationships/relationships';
import { PartnerDTO, RelationshipStatus, RelationshipV2 } from 'support/types';
import { RelationshipBadge } from 'pages/Connections/components/RelationshipBadge/RelationshipBadge';
import { DirectionBadge } from 'components/DirectionBadge/DirectionBadge';
import { PartnerLogo } from 'components/PartnerLogo/PartnerLogo';
import { Sidebar } from 'components/Sidebar/Sidebar';
import { SidebarSection } from 'components/Sidebar/SidebarSection/SidebarSection';
import { SidebarTableContent } from 'components/Sidebar/SidebarTableContent/SidebarTableContent';

import { ConnectorName } from './components/ConnectorName';
import { InternalIdentifierEditor } from './components/InternalIdentifierEditor';

type RelationshipDetailsSidebarProps = {
  relationship: RelationshipV2;
  currentPartner: PartnerDTO;
};

export const RelationshipDetailsSidebar = ({ relationship, currentPartner }: RelationshipDetailsSidebarProps) => {
  const { t } = useTranslation();

  const otherPartner = getTradePartner(relationship, currentPartner.id);
  const direction = relationship.sender_partner?.id === currentPartner.id ? 'OUTBOUND' : 'INBOUND';
  const requestedBy = relationship.trade_request?.requested_by === currentPartner.id ? 'current' : 'other';

  const isLiveOrActive = [RelationshipStatus.LIVE, RelationshipStatus.ACTIVE].includes(
    RelationshipStatus[relationship.status],
  );

  const senderConnector = getConnector(relationship.sender_integration, isLiveOrActive);
  const receiverConnector = getConnector(relationship.receiver_integration, isLiveOrActive);

  const properties: Array<{ label: React.ReactNode; value: React.ReactNode }> = [
    {
      label: t('connections:relationshipDetails.sidebar.partner'),
      value: (
        <div className="flex items-center gap-2">
          <PartnerLogo imageUrl={otherPartner?.logo_url} size="small" />
          <span className="line-clamp-2 break-all">{otherPartner?.name}</span>
        </div>
      ),
    },
    {
      label: t('connections:relationshipDetails.sidebar.status'),
      value: <RelationshipBadge relationship={relationship} currentPartnerId={currentPartner.id} />,
    },
    {
      label: t('connections:relationshipDetails.sidebar.direction'),
      value: <DirectionBadge direction={direction} />,
    },
    {
      label: t('connections:relationshipDetails.sidebar.sendingSystem'),
      value: <ConnectorName connector={senderConnector} />,
    },
    {
      label: t('connections:relationshipDetails.sidebar.receivingSystem'),
      value: <ConnectorName connector={receiverConnector} />,
    },
  ];

  if (isLiveOrActive && relationship.go_live_date) {
    properties.push({
      label: t('connections:relationshipDetails.sidebar.goLiveDate'),
      value: <span className="tabular-nums">{formatInstantAsDay(relationship.go_live_date)}</span>,
    });
  }

  return (
    <Sidebar>
      <SidebarSection header={{ title: t('connections:relationshipDetails.sidebar.title') }}>
        <SidebarTableContent sections={[properties]} rowClassName="mb-0! min-h-10 py-2" />
        {requestedBy === 'current' && <InternalIdentifierEditor relationship={relationship} />}
      </SidebarSection>
    </Sidebar>
  );
};

const getConnector = (
  integration: RelationshipV2['sender_integration'] | RelationshipV2['receiver_integration'],
  isLiveOrActive: boolean,
) =>
  isLiveOrActive
    ? integration?.production?.connector
    : integration?.testing?.connector || integration?.production?.connector;
